import type { StaffAssignmentRow } from "../staff/types";
import type { UserDetail } from "../types";
import {
  MembershipRole,
  type AdminMembership,
  type AdminUserMemberships,
  type MembershipLocation,
} from "@/shared/organizations/membership-types";

/** Locations of one STAFF membership as selectable rows (active and inactive). */
export function membershipLocationRows(membership: AdminMembership): readonly StaffAssignmentRow[] {
  if (membership.role !== MembershipRole.STAFF) return [];
  return membership.locations.map<StaffAssignmentRow>((l) => ({
    kind: l.kind === "VENUE" ? "venue" : "property",
    entityId: l.id,
    entityName: l.name,
    isActive: l.active,
  }));
}

/**
 * Selectable rows for the bulk actions: every location of every STAFF
 * membership, active and inactive. The bulk endpoints address assignments by
 * venue / property id, which is exactly what the memberships read carries.
 */
export function staffLocationRows(
  data: AdminUserMemberships | undefined,
): readonly StaffAssignmentRow[] {
  if (!data) return [];
  return data.memberships.flatMap(membershipLocationRows);
}

/**
 * Locations an OWNER membership covers. The memberships read lists locations
 * for STAFF only, so an owner's venues / properties come from the detail
 * payload the page already holds (it is the owner's own organization).
 */
export function ownedLocationsFor(
  user: UserDetail,
  membership: AdminMembership,
): readonly MembershipLocation[] {
  if (membership.role !== MembershipRole.OWNER) return [];
  const kind = membership.organization.kind;
  const owned = kind === "VENUE" ? user.ownedVenues : user.ownedProperties;
  return owned.map((entity) => ({ kind, id: entity.id, name: entity.name, active: true }));
}
